import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

export default function ParentInfo() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Navigation />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-parent-line"></i>
              <span>For Parents & Guardians</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Parent Information & Safety</h1>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about how SayItBetter helps your teen navigate digital communication safely.
            </p>
          </div>

          {/* What is SayItBetter */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What is SayItBetter?</h2>
            <p className="text-base text-gray-700 mb-4 leading-relaxed">
              SayItBetter is an educational tool designed to help teenagers ages 13-18 develop healthy communication skills in their digital relationships. Using AI-powered analysis, the app helps teens understand the tone and intent behind text messages they receive and provides age-appropriate, respectful reply suggestions.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              Our mission is to empower young people with the skills to set boundaries, recognize unhealthy communication patterns, and build respectful relationships—both online and offline.
            </p>
          </div>

          {/* Safety Features */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Safety & Privacy Features</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">No Data Storage</h3>
                  <p className="text-sm text-gray-700">
                    Messages are analyzed in real-time and immediately discarded. We never store, save, or share the content of messages your teen analyzes. Your teen's privacy is our top priority.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Age-Appropriate Content</h3>
                  <p className="text-sm text-gray-700">
                    All guidance, explanations, and reply suggestions are carefully designed for ages 13-18. We use supportive, non-judgmental language that empowers teens without exposing them to inappropriate content.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Red Flag Detection</h3>
                  <p className="text-sm text-gray-700">
                    Our AI identifies concerning patterns like manipulation, pressure, guilt-tripping, and other unhealthy communication tactics. When red flags are detected, we encourage teens to talk to a trusted adult.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-heart-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Encourages Adult Support</h3>
                  <p className="text-sm text-gray-700">
                    We consistently remind teens that SayItBetter is not a replacement for talking to trusted adults. For serious concerns, we always encourage reaching out to parents, guardians, school counselors, or other trusted adults.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-open-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Educational Focus</h3>
                  <p className="text-sm text-gray-700">
                    Beyond message analysis, we provide bite-sized lessons on healthy communication, boundaries, emotional intelligence, and recognizing unhealthy relationship patterns.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What We Don't Do */}
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-red-900 mb-6">What SayItBetter Does NOT Do</h2>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <i className="ri-close-circle-line text-xl text-red-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm text-red-900">We do not provide therapy or mental health treatment</span>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-close-circle-line text-xl text-red-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm text-red-900">We do not store or monitor your teen's messages</span>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-close-circle-line text-xl text-red-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm text-red-900">We do not provide advice on explicit or sexual content</span>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-close-circle-line text-xl text-red-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm text-red-900">We do not teach manipulation tactics or how to deceive others</span>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-close-circle-line text-xl text-red-600 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm text-red-900">We do not replace parental guidance or professional counseling</span>
              </li>
            </ul>
          </div>

          {/* How to Talk to Your Teen */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Talk to Your Teen About SayItBetter</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Start with curiosity, not concern</h3>
                <p className="text-sm text-gray-700">
                  "I heard about this app that helps with understanding text messages. Have you ever felt confused about how to respond to someone?"
                </p>
              </div>
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Emphasize it's a learning tool</h3>
                <p className="text-sm text-gray-700">
                  "This isn't about monitoring you—it's about giving you tools to handle tricky conversations with confidence."
                </p>
              </div>
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Keep the door open</h3>
                <p className="text-sm text-gray-700">
                  "If you ever get a message that makes you uncomfortable, I'm here to talk about it—no judgment."
                </p>
              </div>
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Respect their privacy</h3>
                <p className="text-sm text-gray-700">
                  "You don't have to show me every message you analyze. I trust you to come to me if something serious comes up."
                </p>
              </div>
            </div>
          </div>

          {/* Warning Signs */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Warning Signs to Watch For</h2>
            <p className="text-sm text-gray-700 mb-6">
              While SayItBetter helps teens recognize unhealthy communication, parents should also be aware of these warning signs:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-yellow-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Behavioral Changes</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Sudden withdrawal from friends or activities</li>
                  <li>• Increased anxiety around phone notifications</li>
                  <li>• Secretive about who they're texting</li>
                  <li>• Changes in sleep or eating patterns</li>
                </ul>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Emotional Signs</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Increased irritability or mood swings</li>
                  <li>• Expressing feelings of worthlessness</li>
                  <li>• Excessive worry about disappointing someone</li>
                  <li>• Fear of setting boundaries</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-gray-700 mt-6">
              If you notice these signs, have a calm, non-judgmental conversation with your teen. Consider reaching out to a school counselor or mental health professional for additional support.
            </p>
          </div>

          {/* Resources */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Additional Resources for Parents</h2>
            <div className="space-y-4">
              <a href="#" className="flex items-center justify-between bg-white rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3">
                  <i className="ri-book-line text-xl text-blue-600"></i>
                  <span className="text-sm font-medium text-gray-900">Common Sense Media: Digital Citizenship Guide</span>
                </div>
                <i className="ri-external-link-line text-gray-400"></i>
              </a>
              <a href="#" className="flex items-center justify-between bg-white rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3">
                  <i className="ri-heart-line text-xl text-blue-600"></i>
                  <span className="text-sm font-medium text-gray-900">Love is Respect: Healthy Relationship Resources</span>
                </div>
                <i className="ri-external-link-line text-gray-400"></i>
              </a>
              <a href="#" className="flex items-center justify-between bg-white rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3">
                  <i className="ri-phone-line text-xl text-blue-600"></i>
                  <span className="text-sm font-medium text-gray-900">Crisis Text Line: Text HOME to 741741</span>
                </div>
                <i className="ri-external-link-line text-gray-400"></i>
              </a>
              <a href="#" className="flex items-center justify-between bg-white rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3">
                  <i className="ri-shield-line text-xl text-blue-600"></i>
                  <span className="text-sm font-medium text-gray-900">National Center for Missing & Exploited Children</span>
                </div>
                <i className="ri-external-link-line text-gray-400"></i>
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions or Concerns?</h2>
            <p className="text-base text-gray-700 mb-6">
              We're here to help. If you have questions about how SayItBetter works or concerns about your teen's safety, please reach out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/disclaimer" className="px-6 py-3 bg-gray-100 text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap">
                View Safety Disclaimer
              </Link>
              <a href="mailto:support@sayitbetter.com" className="px-6 py-3 bg-green-600 text-white text-sm font-semibold rounded-full hover:bg-green-700 transition-colors whitespace-nowrap">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
